import { useMemo, useState } from 'react';
import {
  Box, Flex, Input, Table, Thead, Tbody, Tr, Th, Td, Text, Button, Badge,
  useColorModeValue, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  renameVocabulary, mergeVocabulary, deleteVocabularyValue,
  type VocabularyKind, type VocabularyValueDto,
} from '../../../api/dictionariesApi';
import { ApiError } from '../../../services/apiClient';
import { RenameValueModal, MergeValueModal, ConfirmDeleteModal } from './DictionaryModals';

const HIGH_IMPACT = 100;

/**
 * One free-text vocabulary (genre/mood/tempo/tag): a searchable table of values with their
 * library-track usage counts, plus rename / merge / delete actions that propagate to every
 * track using the value (006 FR-079..FR-082).
 */
export function VocabularySection({ kind, values }: { kind: VocabularyKind; values: VocabularyValueDto[] }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<VocabularyValueDto | null>(null);
  const [mode, setMode] = useState<'rename' | 'merge' | 'delete' | null>(null);

  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? values.filter((v) => v.value.toLowerCase().includes(q)) : values;
  }, [values, search]);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['admin-vocabulary'] });
    queryClient.invalidateQueries({ queryKey: ['admin-library-filter-options'] });
    queryClient.invalidateQueries({ queryKey: ['admin-library'] });
  }

  function close() { setActive(null); setMode(null); }

  function onError(err: unknown, fallbackKey: string) {
    const message = err instanceof ApiError ? err.message : t(fallbackKey);
    toast({ title: message, status: 'error', duration: 4000, isClosable: true });
  }

  function onDone(affected: number) {
    invalidate();
    toast({ title: t('admin.dictionary.applied', { count: affected }), status: 'success', duration: 3000, isClosable: true });
    close();
  }

  const renameMutation = useMutation({
    mutationFn: (newValue: string) => renameVocabulary(kind, active!.value, newValue),
    onSuccess: (r) => onDone(r.affected),
    onError: (e) => onError(e, 'admin.dictionary.rename.failed'),
  });

  const mergeMutation = useMutation({
    mutationFn: (target: string) => mergeVocabulary(kind, [active!.value], target),
    onSuccess: (r) => onDone(r.affected),
    onError: (e) => onError(e, 'admin.dictionary.merge.failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteVocabularyValue(kind, active!.value),
    onSuccess: (r) => onDone(r.affected),
    onError: (e) => onError(e, 'admin.dictionary.delete.failed'),
  });

  const mergeOptions = useMemo(
    () => values
      .filter((v) => v.value !== active?.value)
      .map((v) => ({ value: v.value, label: `${v.value} (${v.trackCount})` })),
    [values, active],
  );

  return (
    <Box data-testid={`vocabulary-section-${kind}`}>
      <Text fontSize="xs" color={secondaryColor} mb={3} data-testid={`vocabulary-note-${kind}`}>
        {t('admin.dictionary.extractedNote')}
      </Text>
      <Input
        size="sm"
        maxW="320px"
        mb={3}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('admin.dictionary.search')}
        data-testid={`vocabulary-search-${kind}`}
      />

      {filtered.length === 0 ? (
        <Text color={secondaryColor} fontSize="sm" data-testid={`vocabulary-empty-${kind}`}>
          {t('admin.dictionary.empty')}
        </Text>
      ) : (
        <Table variant="simple" size="sm" data-testid={`vocabulary-table-${kind}`}>
          <Thead>
            <Tr>
              <Th color={secondaryColor} borderColor={borderColor}>{t('admin.dictionary.value')}</Th>
              <Th color={secondaryColor} borderColor={borderColor} isNumeric>{t('admin.dictionary.trackCount')}</Th>
              <Th color={secondaryColor} borderColor={borderColor} textAlign="right">{t('admin.dictionary.actions')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((v) => (
              <Tr key={v.value} data-testid={`vocabulary-row-${kind}-${v.value}`}>
                <Td borderColor={borderColor}>
                  <Text fontSize="sm" fontWeight="600">{v.value}</Text>
                </Td>
                <Td borderColor={borderColor} isNumeric>
                  <Badge colorScheme="gray">{v.trackCount}</Badge>
                </Td>
                <Td borderColor={borderColor} textAlign="right">
                  <Flex gap={2} justify="flex-end">
                    <Button size="xs" variant="outline" onClick={() => { setActive(v); setMode('rename'); }}
                      data-testid={`vocabulary-rename-${kind}-${v.value}`}>
                      {t('admin.dictionary.rename.action')}
                    </Button>
                    <Button size="xs" variant="outline" isDisabled={values.length < 2}
                      onClick={() => { setActive(v); setMode('merge'); }}
                      data-testid={`vocabulary-merge-${kind}-${v.value}`}>
                      {t('admin.dictionary.merge.action')}
                    </Button>
                    <Button size="xs" variant="outline" colorScheme="red"
                      onClick={() => { setActive(v); setMode('delete'); }}
                      data-testid={`vocabulary-delete-${kind}-${v.value}`}>
                      {t('admin.dictionary.delete.action')}
                    </Button>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {active && mode === 'rename' && (
        <RenameValueModal
          isOpen
          onClose={close}
          currentValue={active.value}
          onSubmit={(v) => renameMutation.mutate(v)}
          isPending={renameMutation.isPending}
        />
      )}
      {active && mode === 'merge' && (
        <MergeValueModal
          isOpen
          onClose={close}
          source={active.value}
          options={mergeOptions}
          onSubmit={(target) => mergeMutation.mutate(target)}
          isPending={mergeMutation.isPending}
        />
      )}
      {active && mode === 'delete' && (
        <ConfirmDeleteModal
          isOpen
          onClose={close}
          value={active.value}
          trackCount={active.trackCount}
          highImpact={active.trackCount > HIGH_IMPACT}
          onConfirm={() => deleteMutation.mutate()}
          isPending={deleteMutation.isPending}
        />
      )}
    </Box>
  );
}
